import React, { useEffect, useState, useRef } from 'react'
import { usePartnerAuth, Partner } from '../../context/PartnerAuthContext'
import { supabase } from '../../lib/supabaseClient'

// ── Types ──────────────────────────────────────────────

type PartnerFormData = {
  address: string
  logo_url: string
  website_url: string
  contact_name: string
  contact_email: string
  contact_phone: string
  color_code: string
  status: 'ACTIVE' | 'DISABLED'
}

// ── Styles ─────────────────────────────────────────────

const styles = {
  card: {
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '0.75rem',
    padding: '2rem',
    marginBottom: '1.5rem',
  } as React.CSSProperties,
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '1.25rem',
    paddingBottom: '0.75rem',
    borderBottom: '1px solid #e2e8f0',
  } as React.CSSProperties,
  formGroup: {
    marginBottom: '1rem',
  } as React.CSSProperties,
  label: {
    display: 'block',
    marginBottom: '0.3rem',
    fontWeight: '500',
    fontSize: '0.9rem',
    color: '#334155',
  } as React.CSSProperties,
  input: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid #cbd5e1',
    borderRadius: '0.375rem',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
  } as React.CSSProperties,
  inputReadonly: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid #e2e8f0',
    borderRadius: '0.375rem',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
    background: '#f8fafc',
    color: '#64748b',
    cursor: 'not-allowed',
  } as React.CSSProperties,
  select: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid #cbd5e1',
    borderRadius: '0.375rem',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
    background: 'white',
  } as React.CSSProperties,
  btnPrimary: {
    padding: '0.6rem 1.5rem',
    background: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '0.95rem',
  } as React.CSSProperties,
  btnDisabled: {
    padding: '0.6rem 1.5rem',
    background: '#94a3b8',
    color: 'white',
    border: 'none',
    borderRadius: '0.375rem',
    cursor: 'not-allowed',
    fontWeight: '500',
    fontSize: '0.95rem',
  } as React.CSSProperties,
  errorBox: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#dc2626',
    padding: '0.75rem 1rem',
    borderRadius: '0.375rem',
    marginBottom: '1rem',
    fontSize: '0.9rem',
  } as React.CSSProperties,
  successBox: {
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    color: '#16a34a',
    padding: '0.75rem 1rem',
    borderRadius: '0.375rem',
    marginBottom: '1rem',
    fontSize: '0.9rem',
  } as React.CSSProperties,
  infoBox: {
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    color: '#1e40af',
    padding: '0.75rem 1rem',
    borderRadius: '0.375rem',
    marginBottom: '1rem',
    fontSize: '0.85rem',
  } as React.CSSProperties,
  colorPreview: (color: string): React.CSSProperties => ({
    display: 'inline-block',
    width: '2rem',
    height: '2rem',
    borderRadius: '0.375rem',
    border: '1px solid #cbd5e1',
    background: color,
    verticalAlign: 'middle',
    marginLeft: '0.5rem',
  }),
  colorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  } as React.CSSProperties,
  twoCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem',
  } as React.CSSProperties,
  logoPreview: {
    width: '80px',
    height: '80px',
    borderRadius: '0.5rem',
    border: '1px solid #e2e8f0',
    objectFit: 'cover' as const,
    marginBottom: '0.5rem',
  } as React.CSSProperties,
  uploadBtn: {
    padding: '0.4rem 0.75rem',
    background: '#f1f5f9',
    color: '#475569',
    border: '1px solid #e2e8f0',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.85rem',
  } as React.CSSProperties,
  badge: (active: boolean): React.CSSProperties => ({
    display: 'inline-block',
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '0.85rem',
    fontWeight: '500',
    background: active ? '#dcfce7' : '#fee2e2',
    color: active ? '#166534' : '#991b1b',
  }),
}

// ── Composant ──────────────────────────────────────────

const PartnerSettingsPage: React.FC = () => {
  const { partner, isAdminPartner } = usePartnerAuth()
  const [form, setForm] = useState<PartnerFormData | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Initialiser le formulaire à partir des données du partenaire (une seule fois)
  useEffect(() => {
    if (partner) {
      setForm((prev) =>
        prev
          ? prev // déjà initialisé → ne pas écraser les modifications utilisateur
          : {
              address: partner.address || '',
              logo_url: partner.logo_url || '',
              website_url: partner.website_url || '',
              contact_name: partner.contact_name || '',
              contact_email: partner.contact_email || '',
              contact_phone: partner.contact_phone || '',
              color_code: partner.color_code || '#1976d2',
              status: partner.status,
            }
      )
    }
  }, [partner])

  if (!partner || !form) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <p>Chargement des paramètres...</p>
      </div>
    )
  }

  const handleChange = (field: keyof PartnerFormData, value: string) => {
    setForm((prev) => (prev ? { ...prev, [field]: value } : prev))
    setError(null)
    setSuccess(null)
  }

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Vérification basique
    if (!file.type.startsWith('image/')) {
      setError('Seules les images sont acceptées (PNG, JPG, SVG)')
      return
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('Le fichier ne doit pas dépasser 2 Mo')
      return
    }

    setUploading(true)
    setError(null)

    try {
      // Essayer l'upload Supabase Storage
      const fileExt = file.name.split('.').pop()
      const filePath = `partners/${partner.id}/logo.${fileExt}`

      const { error: uploadError } = await supabase.storage
        .from('logos')
        .upload(filePath, file, { upsert: true })

      if (uploadError) {
        // Si le bucket n'existe pas, on informe l'utilisateur (stub)
        console.warn('Storage upload failed (bucket may not exist):', uploadError.message)
        setError('Upload logo : le stockage n\'est pas encore configuré. Vous pouvez saisir une URL manuellement.')
        setUploading(false)
        return
      }

      const { data: urlData } = supabase.storage
        .from('logos')
        .getPublicUrl(filePath)

      if (urlData?.publicUrl) {
        handleChange('logo_url', urlData.publicUrl)
        setSuccess('Logo uploadé avec succès')
      }
    } catch (err) {
      console.error('Logo upload error:', err)
      setError('Erreur lors de l\'upload du logo. Vous pouvez saisir une URL manuellement.')
    } finally {
      setUploading(false)
    }
  }

  const handleSave = async () => {
    if (!isAdminPartner) return

    // Validation basique
    if (!form.contact_name.trim()) {
      setError('Le nom du contact est requis')
      return
    }
    if (!form.contact_email.trim()) {
      setError('L\'email du contact est requis')
      return
    }
    if (!form.address.trim()) {
      setError('L\'adresse est requise')
      return
    }

    setSaving(true)
    setError(null)
    setSuccess(null)

    try {
      const { error: updateError } = await supabase
        .from('partners')
        .update({
          address: form.address.trim(),
          logo_url: form.logo_url.trim() || null,
          website_url: form.website_url.trim() || null,
          contact_name: form.contact_name.trim(),
          contact_email: form.contact_email.trim(),
          contact_phone: form.contact_phone.trim() || null,
          color_code: form.color_code,
          status: form.status,
          updated_at: new Date().toISOString(),
        })
        .eq('id', partner.id)

      if (updateError) {
        console.error('Update partner error:', updateError)
        setError(`Erreur lors de la sauvegarde : ${updateError.message}`)
      } else {
        setSuccess('Paramètres sauvegardés avec succès. Rechargez la page pour voir les changements de couleur.')
      }
    } catch (err) {
      console.error('Save error:', err)
      setError('Erreur inattendue lors de la sauvegarde')
    } finally {
      setSaving(false)
    }
  }

  const inputStyle = isAdminPartner ? styles.input : styles.inputReadonly

  return (
    <div data-testid="settings-page">
      {/* Titre */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1e293b', margin: 0 }}>
          ⚙️ Paramètres du partenaire
        </h2>
        <p style={{ color: '#64748b', fontSize: '0.9rem', marginTop: '0.25rem' }}>
          {isAdminPartner
            ? 'Gérez les informations et l\'apparence de votre organisation.'
            : 'Consultez les informations de votre organisation (lecture seule).'}
        </p>
      </div>

      {/* Banner lecture seule pour USER_PARTNER */}
      {!isAdminPartner && (
        <div style={styles.infoBox} data-testid="readonly-banner">
          🔒 Vous êtes membre : ces paramètres sont en lecture seule. Contactez un administrateur pour les modifier.
        </div>
      )}

      {/* Messages d'erreur / succès */}
      {error && <div style={styles.errorBox} data-testid="settings-error">{error}</div>}
      {success && <div style={styles.successBox} data-testid="settings-success">{success}</div>}

      {/* Section: Informations générales */}
      <div style={styles.card} data-testid="section-general">
        <h3 style={styles.sectionTitle}>📋 Informations générales</h3>

        <div style={styles.formGroup}>
          <label style={styles.label}>Nom du partenaire</label>
          <input
            type="text"
            value={partner.name}
            readOnly
            style={styles.inputReadonly}
            data-testid="settings-name"
          />
          <small style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
            Le nom ne peut être modifié que par l'administrateur StringNow.
          </small>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Adresse</label>
          <input
            type="text"
            value={form.address}
            readOnly={!isAdminPartner}
            onChange={(e) => handleChange('address', e.target.value)}
            style={inputStyle}
            data-testid="settings-address"
            placeholder="Adresse du partenaire"
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Site web</label>
          <input
            type="url"
            value={form.website_url}
            readOnly={!isAdminPartner}
            onChange={(e) => handleChange('website_url', e.target.value)}
            style={inputStyle}
            data-testid="settings-website"
            placeholder="https://www.example.com"
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Statut</label>
          {isAdminPartner ? (
            <select
              value={form.status}
              onChange={(e) => handleChange('status', e.target.value)}
              style={styles.select}
              data-testid="settings-status"
            >
              <option value="ACTIVE">Actif</option>
              <option value="DISABLED">Désactivé</option>
            </select>
          ) : (
            <span style={styles.badge(form.status === 'ACTIVE')} data-testid="settings-status">
              {form.status === 'ACTIVE' ? 'Actif' : 'Désactivé'}
            </span>
          )}
        </div>
      </div>

      {/* Section: Contact */}
      <div style={styles.card} data-testid="section-contact">
        <h3 style={styles.sectionTitle}>📞 Informations de contact</h3>

        <div style={styles.twoCol}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Nom du contact</label>
            <input
              type="text"
              value={form.contact_name}
              readOnly={!isAdminPartner}
              onChange={(e) => handleChange('contact_name', e.target.value)}
              style={inputStyle}
              data-testid="settings-contact-name"
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Email du contact</label>
            <input
              type="email"
              value={form.contact_email}
              readOnly={!isAdminPartner}
              onChange={(e) => handleChange('contact_email', e.target.value)}
              style={inputStyle}
              data-testid="settings-contact-email"
            />
          </div>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Téléphone</label>
          <input
            type="tel"
            value={form.contact_phone}
            readOnly={!isAdminPartner}
            onChange={(e) => handleChange('contact_phone', e.target.value)}
            style={inputStyle}
            data-testid="settings-contact-phone"
            placeholder="+41 XX XXX XX XX"
          />
        </div>
      </div>

      {/* Section: Apparence */}
      <div style={styles.card} data-testid="section-appearance">
        <h3 style={styles.sectionTitle}>🎨 Apparence</h3>

        {/* Logo */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Logo</label>
          {form.logo_url && (
            <img
              src={form.logo_url}
              alt="Logo partenaire"
              style={styles.logoPreview}
              data-testid="settings-logo-preview"
            />
          )}
          {isAdminPartner && (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                style={styles.uploadBtn}
                disabled={uploading}
                data-testid="settings-logo-upload-btn"
              >
                {uploading ? 'Upload en cours…' : '📤 Uploader un logo'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleLogoUpload}
                data-testid="settings-logo-file-input"
              />
            </div>
          )}
          <input
            type="url"
            value={form.logo_url}
            readOnly={!isAdminPartner}
            onChange={(e) => handleChange('logo_url', e.target.value)}
            style={inputStyle}
            data-testid="settings-logo-url"
            placeholder="https://example.com/logo.png"
          />
          <small style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
            URL directe ou upload d'image (PNG, JPG, SVG — max 2 Mo)
          </small>
        </div>

        {/* Couleur */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Couleur de marque</label>
          <div style={styles.colorRow}>
            <input
              type="color"
              value={form.color_code}
              disabled={!isAdminPartner}
              onChange={(e) => handleChange('color_code', e.target.value)}
              style={{ width: '3rem', height: '2.25rem', padding: '0.15rem', cursor: isAdminPartner ? 'pointer' : 'not-allowed' }}
              data-testid="settings-color-picker"
            />
            <input
              type="text"
              value={form.color_code}
              readOnly={!isAdminPartner}
              onChange={(e) => handleChange('color_code', e.target.value)}
              style={{ ...inputStyle, width: '120px' }}
              data-testid="settings-color-code"
              maxLength={7}
              pattern="#[0-9a-fA-F]{6}"
            />
            <div style={styles.colorPreview(form.color_code)} data-testid="settings-color-preview" />
            <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
              Appliquée au header et footer du portail
            </span>
          </div>
        </div>
      </div>

      {/* Bouton sauvegarder (ADMIN_PARTNER uniquement) */}
      {isAdminPartner && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={saving ? styles.btnDisabled : styles.btnPrimary}
            data-testid="settings-save-btn"
          >
            {saving ? 'Sauvegarde en cours…' : '💾 Sauvegarder les paramètres'}
          </button>
        </div>
      )}
    </div>
  )
}

export default PartnerSettingsPage
